import { FC, useEffect, useRef, useState } from 'react';
import {
  StyledAutoComplete,
  StyledAutoCompleteItem,
  StyledAutoCompleteWrapper,
} from './AutoCompleteInput.styles';
import { Input, InputProps } from '../../';
import { identity, includes, pipe, toLower, either, propOr } from 'ramda';

export interface AutoCompleteItem {
  label: string;
  secondaryLabel?: string;
  id: string;
}
export interface AutoCompleteInputProps extends InputProps {
  /** the list of items to show in the autocomplete */
  items: AutoCompleteItem[];
  /** a callback to call when an item is selected */
  onSelectItem?: (item: AutoCompleteItem) => void;
}

/**
 * A text <Input> component which shows an autocomplete with the
 * provided values when focused.
 */
export const AutoCompleteInput: FC<AutoCompleteInputProps> = ({
  items,
  onSelectItem,
  ...otherProps
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [popupVisible, setPopupVisible] = useState<boolean>(false);
  const showPopup = () => setPopupVisible(true);
  const hidePopup = () => setPopupVisible(false);
  const handleClick = (item: AutoCompleteItem) => () => {
    if (onSelectItem) {
      onSelectItem(item);
    }
    if (inputRef.current) {
      inputRef.current.value = item.label;
    }
    hidePopup();
  };
  const matchesQuery = (propName: string) =>
    pipe(propOr('', propName), toLower, includes(searchQuery.toLowerCase()));
  const filterByQuery = either(
    matchesQuery('secondaryLabel'),
    matchesQuery('label'),
  );
  useEffect(() => {
    if (popupVisible) {
      document.body.addEventListener('click', hidePopup);
    } else {
      document.body.removeEventListener('click', hidePopup);
    }
    return () => {
      document.body.removeEventListener('click', hidePopup);
    };
  }, [popupVisible]);
  return (
    <StyledAutoCompleteWrapper>
      <Input
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={inputRef as any}
        data-testid="autocomplete-input"
        onClick={(e) => e.stopPropagation()}
        onFocus={showPopup}
        autoComplete="off"
        onKeyUp={(e: React.KeyboardEvent<HTMLInputElement>) =>
          setSearchQuery(e.currentTarget.value)
        }
        {...otherProps}
      />
      {popupVisible && (
        <StyledAutoComplete>
          {items?.length === 0 && searchQuery && (
            <li style={{ pointerEvents: 'none' }}>
              <StyledAutoCompleteItem>No results found</StyledAutoCompleteItem>
            </li>
          )}
          {items?.length === 0 && !searchQuery && (
            <li style={{ pointerEvents: 'none' }}>
              <StyledAutoCompleteItem>Loading&hellip;</StyledAutoCompleteItem>
            </li>
          )}
          {items?.filter(searchQuery ? filterByQuery : identity).map((item) => (
            <li key={item.id}>
              <StyledAutoCompleteItem
                onClick={handleClick(item)}
                data-testid="autocomplete-list-item"
              >
                <span>{item.label}</span>
                {item.secondaryLabel && <span>{item.secondaryLabel}</span>}
              </StyledAutoCompleteItem>
            </li>
          ))}
        </StyledAutoComplete>
      )}
    </StyledAutoCompleteWrapper>
  );
};

export default AutoCompleteInput;
